import { env } from "../lib/env";
import { supabase } from "../lib/supabase";
import {
  fakeCustomerDashboardMetrics,
  fakeCustomerToListRow,
  listFakeCustomerRecords,
} from "../data/fake-customer-store";
import {
  commissions,
  contracts,
  integrations,
  invoices,
  sales,
  telemedicine,
} from "../data/mock-data";

export interface DashboardMetricRow {
  label: string;
  value: string;
  delta: string;
}

export interface CustomerListRow {
  id: string;
  name: string;
  holder: string;
  phone: string;
  funeral: string;
  telemedicine: string;
  seller: string;
  balance: string;
}

export interface SaleListRow {
  id: string;
  createdAt: string;
  customer: string;
  offer: string;
  seller: string;
  source: string;
  status: string;
  amount: string;
}

export interface ContractListRow {
  number: string;
  customer: string;
  type: string;
  status: string;
  version: string;
  updatedAt: string;
}

export interface InvoiceListRow {
  number: string;
  customer: string;
  products: string;
  dueDate: string;
  total: string;
  status: string;
  method: string;
}

export interface CommissionListRow {
  seller: string;
  product: string;
  sale: string;
  amount: string;
  status: string;
}

export interface TelemedicineListRow {
  person: string;
  family: string;
  externalId: string;
  status: string;
  firstAccess: string;
}

export interface IntegrationListRow {
  provider: string;
  mode: string;
  status: string;
  lastEvent: string;
}

export interface TeamListRow {
  id: string;
  name: string;
  role: string;
  team: string;
  referral: string;
  sales: number;
  status: string;
}

export interface PlatformReadRepository {
  dashboard(): Promise<DashboardMetricRow[]>;
  customers(): Promise<CustomerListRow[]>;
  sales(): Promise<SaleListRow[]>;
  contracts(): Promise<ContractListRow[]>;
  invoices(): Promise<InvoiceListRow[]>;
  commissions(): Promise<CommissionListRow[]>;
  telemedicine(): Promise<TelemedicineListRow[]>;
  integrations(): Promise<IntegrationListRow[]>;
  team(): Promise<TeamListRow[]>;
}

function currency(cents: number | string | bigint | null | undefined): string {
  const normalized = cents === null || cents === undefined ? 0n : BigInt(cents);
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(normalized) / 100);
}

function date(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const options: Intl.DateTimeFormatOptions = { dateStyle: "short" };
  if (value.includes("T")) options.timeStyle = "short";
  return new Intl.DateTimeFormat("pt-BR", options).format(parsed);
}

function textValue(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  return fallback;
}

class MockPlatformReadRepository implements PlatformReadRepository {
  async dashboard() {
    return fakeCustomerDashboardMetrics();
  }
  async customers() {
    return listFakeCustomerRecords().map(fakeCustomerToListRow);
  }
  async sales() {
    return sales;
  }
  async contracts() {
    return contracts;
  }
  async invoices() {
    return invoices;
  }
  async commissions() {
    return commissions;
  }
  async telemedicine() {
    return telemedicine;
  }
  async integrations() {
    return integrations;
  }
  async team(): Promise<TeamListRow[]> {
    return [
      {
        id: "1",
        name: "Vendedor Demo 01",
        role: "Vendedor",
        team: "Equipe Principal",
        referral: "VENDEDOR01",
        sales: 18,
        status: "ATIVO",
      },
      {
        id: "2",
        name: "Vendedor Demo 02",
        role: "Vendedor",
        team: "Equipe Principal",
        referral: "VENDEDOR02",
        sales: 14,
        status: "ATIVO",
      },
      {
        id: "3",
        name: "Supervisora Demo",
        role: "Supervisora",
        team: "Equipe Principal",
        referral: "SUPERVISAO",
        sales: 0,
        status: "ATIVO",
      },
    ];
  }
}

class SupabasePlatformReadRepository implements PlatformReadRepository {
  async dashboard(): Promise<DashboardMetricRow[]> {
    const [households, funeral, telemedicineResult, invoiceResult, commissionResult] =
      await Promise.all([
        supabase.from("v_household_summary").select("id", { count: "exact", head: true }),
        supabase
          .from("v_household_summary")
          .select("id", { count: "exact", head: true })
          .not("funeral_status", "is", null),
        supabase
          .from("v_household_summary")
          .select("id", { count: "exact", head: true })
          .eq("telemedicine_status", "ACTIVE"),
        supabase.from("v_invoice_summary").select("status,total_cents,paid_cents"),
        supabase.from("v_commission_summary").select("status,amount_cents"),
      ]);

    for (const result of [
      households,
      funeral,
      telemedicineResult,
      invoiceResult,
      commissionResult,
    ]) {
      if (result.error) throw result.error;
    }

    const invoiceRows = invoiceResult.data as Array<{
      status: string;
      total_cents: number | string;
      paid_cents: number | string;
    }>;
    const received = invoiceRows.reduce((sum, row) => sum + BigInt(row.paid_cents), 0n);
    const total = invoiceRows.reduce((sum, row) => sum + BigInt(row.total_cents), 0n);
    const overdue = invoiceRows
      .filter((row) => row.status === "OVERDUE")
      .reduce((sum, row) => sum + BigInt(row.total_cents) - BigInt(row.paid_cents), 0n);
    const rate = total === 0n ? 0 : Number((overdue * 10000n) / total) / 100;
    const eligible = (
      commissionResult.data as Array<{ status: string; amount_cents: number | string }>
    )
      .filter((row) => ["ELIGIBLE", "APPROVED"].includes(row.status))
      .reduce((sum, row) => sum + BigInt(row.amount_cents), 0n);

    return [
      { label: "Famílias ativas", value: String(households.count ?? 0), delta: "Base acessível" },
      {
        label: "Planos funerários",
        value: String(funeral.count ?? 0),
        delta: "Contratos cadastrados",
      },
      {
        label: "Telemedicina",
        value: String(telemedicineResult.count ?? 0),
        delta: "Vidas/famílias ativas",
      },
      { label: "Recebido", value: currency(received), delta: "Ledger local" },
      {
        label: "Inadimplência",
        value: `${rate.toFixed(2).replace(".", ",")}%`,
        delta: currency(overdue),
      },
      { label: "Comissão elegível", value: currency(eligible), delta: "Aguardando lote" },
    ];
  }

  async customers(): Promise<CustomerListRow[]> {
    const result = await supabase
      .from("v_household_summary")
      .select("*")
      .order("updated_at", { ascending: false });
    if (result.error) throw result.error;
    return (result.data as Array<Record<string, unknown>>).map((row) => ({
      id: textValue(row.id),
      name: textValue(row.display_name, "Família"),
      holder: textValue(row.holder_name, "Titular não informado"),
      phone: "Contato protegido",
      funeral: textValue(row.funeral_status, "NÃO CONTRATADO"),
      telemedicine: textValue(row.telemedicine_status, "NÃO CONTRATADA"),
      seller: textValue(row.seller_name, "Sem atribuição"),
      balance: row.financial_status === "VENCIDO" ? "Vencido" : "Em dia",
    }));
  }

  async sales(): Promise<SaleListRow[]> {
    const result = await supabase
      .from("v_order_summary")
      .select("*")
      .order("created_at", { ascending: false });
    if (result.error) throw result.error;
    return (result.data as Array<Record<string, unknown>>).map((row) => ({
      id: textValue(row.id),
      createdAt: date(textValue(row.created_at)),
      customer: textValue(row.customer_name ?? row.household_name, "Cliente"),
      offer: textValue(row.offer_name, "Oferta"),
      seller: textValue(row.seller_name, "Sem vendedor"),
      source: textValue(row.source, "MANUAL"),
      status: textValue(row.status, "DRAFT"),
      amount: currency(row.total_first_cycle_cents as number | string | undefined),
    }));
  }

  async contracts(): Promise<ContractListRow[]> {
    const result = await supabase
      .from("v_contract_summary")
      .select("*")
      .order("created_at", { ascending: false });
    if (result.error) throw result.error;
    return (result.data as Array<Record<string, unknown>>).map((row) => ({
      number: textValue(row.number),
      customer: textValue(row.customer_name ?? row.household_name, "Cliente"),
      type: textValue(row.contract_type),
      status: textValue(row.status),
      version: "Snapshot",
      updatedAt: date(textValue(row.signed_at ?? row.created_at)),
    }));
  }

  async invoices(): Promise<InvoiceListRow[]> {
    const result = await supabase
      .from("v_invoice_summary")
      .select("*")
      .order("due_date", { ascending: false });
    if (result.error) throw result.error;
    return (result.data as Array<Record<string, unknown>>).map((row) => ({
      number: textValue(row.number),
      customer: textValue(row.customer_name ?? row.household_name, "Cliente"),
      products: textValue(row.products, "—"),
      dueDate: date(textValue(row.due_date)),
      total: currency(row.total_cents as number | string | undefined),
      status: textValue(row.status),
      method: textValue(row.payment_method, "—"),
    }));
  }

  async commissions(): Promise<CommissionListRow[]> {
    const result = await supabase
      .from("v_commission_summary")
      .select("*")
      .order("created_at", { ascending: false });
    if (result.error) throw result.error;
    return (result.data as Array<Record<string, unknown>>).map((row) => ({
      seller: textValue(row.seller_name, "Vendedor"),
      product: textValue(row.product_code),
      sale: textValue(row.order_number),
      amount: currency(row.amount_cents as number | string | undefined),
      status: textValue(row.status),
    }));
  }

  async telemedicine(): Promise<TelemedicineListRow[]> {
    const result = await supabase
      .from("v_telemedicine_summary")
      .select("*")
      .order("last_synced_at", { ascending: false });
    if (result.error) throw result.error;
    return (result.data as Array<Record<string, unknown>>).map((row) => ({
      person: textValue(row.beneficiary_name),
      family: textValue(row.household_name),
      externalId: textValue(row.external_id),
      status: textValue(row.status),
      firstAccess: row.provisioned_at ? "Concluído" : "Pendente",
    }));
  }

  async integrations(): Promise<IntegrationListRow[]> {
    const result = await supabase.from("v_integration_summary").select("*").order("provider");
    if (result.error) throw result.error;
    return (result.data as Array<Record<string, unknown>>).map((row) => ({
      provider: textValue(row.provider),
      mode: textValue(row.mode),
      status: textValue(row.status),
      lastEvent: date(textValue(row.last_event_at ?? row.last_health_check_at)),
    }));
  }

  async team(): Promise<TeamListRow[]> {
    const result = await supabase.from("v_team_summary").select("*").order("full_name");
    if (result.error) throw result.error;
    return (result.data as Array<Record<string, unknown>>).map((row) => ({
      id: textValue(row.id),
      name: textValue(row.full_name),
      role: "Vendedor",
      team: textValue(row.team_name, "Sem equipe"),
      referral: textValue(row.referral_code),
      sales: Number(row.sales_this_month ?? 0),
      status:
        textValue(row.status) === "ACTIVE"
          ? "ATIVO"
          : textValue(row.status) === "SUSPENDED"
            ? "INATIVO"
            : textValue(row.status) === "INACTIVE"
              ? "ENCERRADO"
              : "PENDENTE",
    }));
  }
}

export const platformRepository: PlatformReadRepository = env.useMocks
  ? new MockPlatformReadRepository()
  : new SupabasePlatformReadRepository();
